import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { VideoStatusBadge } from '../../components/StatusBadge';
import { Select } from '../../components/Select';
import { formatDate, formatDateTime, formatShortDate } from '../../lib/format';
import type { CreatorAccount, Message, SigningDocumentSummary } from '../../lib/types';
import styles from './Dashboard.module.css';

const FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'awaiting_submission', label: 'Awaiting Video' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'revisions_requested', label: 'Revisions Requested' },
  { key: 'approved', label: 'Approved' },
];

export type DetailTab =
  | 'application'
  | 'documents'
  | 'medical-intake'
  | 'brand-guidelines'
  | 'video'
  | 'messages'
  | 'payment';

export const DETAIL_TABS: readonly DetailTab[] = [
  'application',
  'documents',
  'medical-intake',
  'brand-guidelines',
  'video',
  'messages',
  'payment',
] as const;

export function productOf(a: CreatorAccount): string {
  const p = a.submission?.productInterest;
  if (!p) return '—';
  return Array.isArray(p) ? p.join(', ') || '—' : String(p) || '—';
}

export function ActiveCreatorsTab() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['admin', 'creators'],
    queryFn: () => api.get<CreatorAccount[]>('/api/admin/creators'),
  });
  const accounts = data ?? [];

  const [filter, setFilter] = useState('');

  const filtered = useMemo(
    () => (filter ? accounts.filter((a) => a.video_status === filter) : accounts),
    [accounts, filter],
  );

  return (
    <>
      <div className={styles.intakeTopbar}>
        <div className={styles.pillRow}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.iPill} ${filter === f.key ? styles.iPillActive : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className={styles.intakeTotal}>
          {filtered.length} creator{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className={styles.tableWrap}>
        {filtered.length === 0 ? (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>👥</div>
            <div>No active creator accounts yet</div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Creator</th>
                <th>Product</th>
                <th>Documents</th>
                <th>Intake</th>
                <th>Guidelines</th>
                <th>Video Status</th>
                <th>Messages</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <AcRow
                  key={a.id}
                  a={a}
                  onSelect={() => navigate(`/admin/creators/${a.id}`)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function AcRow({ a, onSelect }: { a: CreatorAccount; onSelect: () => void }) {
  const msgCount = useQuery({
    queryKey: ['admin', 'creators', a.id, 'messages'],
    queryFn: () =>
      api.get<{ messages: Message[] } | Message[]>(
        `/api/admin/creators/${a.id}/messages`,
      ),
    enabled: false,
  });
  const count = Array.isArray(msgCount.data)
    ? msgCount.data.length
    : msgCount.data?.messages?.length ?? 0;
  const name = a.submission?.fullName || a.email;
  return (
    <tr onClick={onSelect}>
      <td>
        <div style={{ fontWeight: 600 }}>{name}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.email}</div>
      </td>
      <td>{productOf(a)}</td>
      <td style={{ textAlign: 'center' }}>
        <span
          className={`${styles.acStepIcon} ${a.all_documents_signed ? styles.acStepDone : styles.acStepTodo}`}
        >
          {a.all_documents_signed ? '✓' : '○'}
        </span>
      </td>
      <td style={{ textAlign: 'center' }}>
        <span
          className={`${styles.acStepIcon} ${a.intake_complete ? styles.acStepDone : styles.acStepTodo}`}
        >
          {a.intake_complete ? '✓' : '○'}
        </span>
      </td>
      <td style={{ textAlign: 'center' }}>
        <span
          className={`${styles.acStepIcon} ${a.brand_guidelines_acknowledged ? styles.acStepDone : styles.acStepTodo}`}
        >
          {a.brand_guidelines_acknowledged ? '✓' : '○'}
        </span>
      </td>
      <td>
        <VideoStatusBadge status={a.video_status} />
      </td>
      <td style={{ textAlign: 'center' }}>
        <span className={`${styles.acMsgCount} ${count === 0 ? styles.acMsgZero : ''}`}>
          {count}
        </span>
      </td>
      <td>{formatShortDate(a.createdAt)}</td>
    </tr>
  );
}

export interface AcDetailProps {
  a: CreatorAccount;
  tab: DetailTab;
  onTab: (t: DetailTab) => void;
  onBack: () => void;
  onPatch: (body: Record<string, unknown>, successMsg?: string) => void;
  toast: (msg: string) => void;
}

export function AcDetail({ a, tab, onTab, onBack, onPatch, toast }: AcDetailProps) {
  const stepPills = [
    { label: 'Documents', done: a.all_documents_signed },
    { label: 'Medical Intake', done: a.intake_complete },
    { label: 'Brand Guidelines', done: a.brand_guidelines_acknowledged },
    { label: 'Video Approved', done: a.video_status === 'approved' },
    { label: 'Payment Sent', done: a.payment_status === 'paid' },
  ];

  // Render the detail header, tab bar, and scrollable content as flat
  // siblings of the parent `.main` flex column — same shape the dashboard
  // tabs use. An earlier version wrapped these in an extra
  // <div style={{flex:1, minHeight:0}}> which broke the scroll chain on
  // the Documents tab (content past the viewport became unreachable).
  return (
    <>
      <div className={styles.acDetailHeader}>
        <button type="button" className={styles.acBackBtn} onClick={onBack}>
          ← All Creators
        </button>
        <div className={styles.acDetailRow}>
          <div>
            <div className={styles.acDetailName}>{a.submission?.fullName || a.email}</div>
            <div className={styles.acDetailEmail}>{a.email}</div>
          </div>
          {productOf(a) !== '—' && (
            <span
              className={styles.vsBadge}
              style={{ background: '#fef3c7', color: '#92400e' }}
            >
              {productOf(a)}
            </span>
          )}
        </div>
        <div className={styles.acDetailSteps}>
          {stepPills.map((s) => (
            <span
              key={s.label}
              className={`${styles.acStepPill} ${s.done ? styles.acStepPillDone : styles.acStepPillTodo}`}
            >
              {s.done ? '✓' : '○'} {s.label}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.acPageTabs}>
        {(
          [
            ['application', 'Application'],
            ['documents', 'Documents'],
            ['medical-intake', 'Medical Intake'],
            ['brand-guidelines', 'Brand Guidelines'],
            ['video', 'Video'],
            ['messages', 'Messages'],
            ['payment', 'Payment'],
          ] as [DetailTab, string][]
        ).map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            className={`${styles.acPageTab} ${tab === k ? styles.acPageTabActive : ''}`}
            onClick={() => onTab(k)}
          >
            {lbl}
          </button>
        ))}
      </div>
      <div className={styles.acPageContent}>
        {tab === 'application' && <ApplicationTab a={a} />}
        {tab === 'documents' && <DocumentsTab a={a} toast={toast} />}
        {tab === 'medical-intake' && <MedicalTab a={a} onPatch={onPatch} toast={toast} />}
        {tab === 'brand-guidelines' && <GuidelinesTab a={a} />}
        {tab === 'video' && <VideoTab a={a} onPatch={onPatch} />}
        {tab === 'messages' && <MessagesTab a={a} toast={toast} />}
        {tab === 'payment' && <PaymentTab a={a} onPatch={onPatch} />}
      </div>
    </>
  );
}

function productLabel(p: string[] | string | undefined): string {
  const arr = Array.isArray(p) ? p : p ? String(p).split(',') : [];
  return arr
    .map((s) => s.toString().trim())
    .filter(Boolean)
    .map((s) =>
      s.includes('Beta Blocker') ? 'Beta Blockers' : s.includes('Sleep') ? 'Sleep Plan' : s,
    )
    .join(', ');
}

function ApplicationTab({ a }: { a: CreatorAccount }) {
  const s = a.submission;
  if (!s) {
    return (
      <div className={styles.acTabBody}>
        <div className={styles.acCard}>
          <div className={styles.acCardLabel}>No application on file</div>
          <p className={styles.contractSendDesc}>
            This creator account has no linked intake submission.
          </p>
        </div>
      </div>
    );
  }
  const agreements: [boolean, string][] = [
    [s.agreedRate, 'Agreed to $300 flat rate'],
    [s.agreedRevisions, 'Agreed to revision process'],
    [s.agreedIntake, 'Agreed to online intake (~15 min)'],
  ];
  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Application</div>
        <div className={styles.acTabSub}>
          Submitted {formatShortDate(s.submittedAt)} · the creator's original intake responses.
        </div>
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Platforms &amp; Followers</div>
        {a.platformHandles?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {a.platformHandles.map((h) => (
              <div key={h.id} style={{ fontSize: 13 }}>
                <strong>{h.platformName}</strong> {h.handle}
                {h.followerCount > 0 ? ` — ${h.followerCount.toLocaleString()} followers` : ''}
                {h.platformLink && (
                  <a
                    href={h.platformLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2563eb', marginLeft: 6 }}
                  >
                    ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>—</div>
        )}
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Main Account Link</div>
        <div style={{ fontSize: 13 }}>
          {s.mainLink ? (
            <a href={s.mainLink} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>
              {s.mainLink}
            </a>
          ) : (
            '—'
          )}
        </div>
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Product Interest</div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          {productLabel(s.productInterest) || '—'}
        </div>
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>How they describe their content</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
          {s.contentDescription || '—'}
        </div>
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>How they describe their audience</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
          {s.audienceDescription || '—'}
        </div>
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Video Idea</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
          {s.hasIdeas === 'yes'
            ? 'Has their own concept'
            : s.hasIdeas === 'no'
              ? 'Needs help with concepts'
              : '—'}
        </div>
        {s.hasIdeas === 'yes' && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text2)',
              whiteSpace: 'pre-wrap',
              paddingTop: 8,
              borderTop: '1px solid var(--border, #e2e8f0)',
            }}
          >
            {s.ideaDetail || '—'}
          </div>
        )}
        {s.hasIdeas === 'no' && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text2)',
              whiteSpace: 'pre-wrap',
              paddingTop: 8,
              borderTop: '1px solid var(--border, #e2e8f0)',
            }}
          >
            {s.helpDetail || '—'}
          </div>
        )}
      </div>

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Agreement Confirmations</div>
        <div className={styles.agrStack}>
          {agreements.map(([val, label], i) => (
            <div
              key={i}
              className={`${styles.agrRow} ${val ? styles.agrYes : styles.agrNo}`}
            >
              <span className={styles.agrIcon}>{val ? '✓' : '✗'}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {s.extraNotes && (
        <div className={styles.acCard}>
          <div className={styles.acCardLabel}>Creator Notes</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
            {s.extraNotes}
          </div>
        </div>
      )}

      {s.internalNotes && (
        <div className={styles.acCard}>
          <div className={styles.acCardLabel}>Internal Review Notes</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
            {s.internalNotes}
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ a, toast }: { a: CreatorAccount; toast: (m: string) => void }) {
  const qc = useQueryClient();
  const docs = useQuery({
    queryKey: ['admin', 'creators', a.id, 'documents'],
    queryFn: () =>
      api.get<SigningDocumentSummary[]>(`/api/admin/creators/${a.id}/documents`),
  });
  const resend = useMutation({
    mutationFn: () =>
      api.post<SigningDocumentSummary[]>(`/api/admin/creators/${a.id}/documents/resend`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'creators', a.id, 'documents'] });
      qc.invalidateQueries({ queryKey: ['admin', 'creators'] });
      toast('Documents refreshed');
    },
    onError: () => toast('Failed to resend documents'),
  });

  const rows = docs.data ?? [];
  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Partnership Documents</div>
        <div className={styles.acTabSub}>
          DocuSeal submissions for this creator's legal documents.
        </div>
      </div>

      {docs.isPending && (
        <div className={styles.acCard}>
          <div className={styles.acCardLabel}>Loading…</div>
        </div>
      )}

      {docs.isSuccess && rows.length === 0 && (
        <div className={styles.acCard}>
          <div className={styles.acCardLabel}>No documents yet</div>
          <p className={styles.contractSendDesc}>
            No DocuSeal submissions have been minted for this creator. Use the
            button below to send the active document templates.
          </p>
        </div>
      )}

      {rows.map((d) => (
        <div className={styles.acCard} key={d.id}>
          <div className={styles.acCardLabel}>{d.template_name}</div>
          {d.status === 'completed' ? (
            <div className={styles.contractSignedCard}>
              <div className={styles.contractSignedBadge}>✓ Signed</div>
              <div className={styles.contractSignedRow}>
                <span className={styles.contractFieldLabel}>Signed</span>
                <span className={styles.contractFieldVal}>
                  {d.completed_at ? formatDate(d.completed_at) : '—'}
                </span>
              </div>
              <div className={styles.contractSignedRow}>
                <span className={styles.contractFieldLabel}>Creator</span>
                <span className={styles.contractFieldVal}>{a.email}</span>
              </div>
              {d.signed_pdf_url && (
                <a
                  href={d.signed_pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginTop: 10, display: 'inline-block', color: '#2563eb' }}
                >
                  View signed PDF
                </a>
              )}
              {d.audit_log_url && (
                <a
                  href={d.audit_log_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginTop: 4, marginLeft: 12, display: 'inline-block', color: '#64748b', fontSize: 12 }}
                >
                  Audit log
                </a>
              )}
            </div>
          ) : (
            <span
              className={styles.vsBadge}
              style={{ background: '#fef3c7', color: '#92400e' }}
            >
              {d.status === 'pending'
                ? 'Awaiting Signature'
                : d.status === 'declined'
                  ? 'Declined'
                  : 'Expired'}
            </span>
          )}
        </div>
      ))}

      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Send / Refresh</div>
        <div className={styles.contractSendBlock}>
          <div className={styles.contractSendIcon}>📄</div>
          <p className={styles.contractSendDesc}>
            Mint a DocuSeal submission for any active template this creator
            doesn't already have. Existing submissions are left untouched.
          </p>
          <button
            type="button"
            className={styles.btnDocusign}
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
          >
            {resend.isPending ? 'Sending…' : 'Send missing documents'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MedicalTab({
  a,
  onPatch,
  toast,
}: {
  a: CreatorAccount;
  onPatch: (body: Record<string, unknown>, msg?: string) => void;
  toast: (m: string) => void;
}) {
  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Medical Intake</div>
        <div className={styles.acTabSub}>
          Track the creator's intake completion and access their unique code.
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Intake Status</div>
        {a.intake_complete ? (
          <div className={styles.acCompleteCard}>
            <span className={styles.acCompleteCheck}>✓</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Medical intake complete</span>
          </div>
        ) : (
          <span className={styles.vsBadge} style={{ background: '#fef3c7', color: '#92400e' }}>
            Not yet completed
          </span>
        )}
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            className={`${styles.acProgToggle} ${a.intake_complete ? styles.acMarkUndone : styles.acMarkDone}`}
            onClick={() =>
              onPatch({ intake_complete: !a.intake_complete }, 'Saved')
            }
          >
            {a.intake_complete ? 'Mark as Incomplete' : 'Mark as Complete'}
          </button>
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>One-Time Intake Code</div>
        <div className={styles.acIntakeCodeRow}>
          <code className={styles.acIntakeCode}>{a.intakeCode || '—'}</code>
          <button
            type="button"
            className={styles.btnCopy}
            onClick={() => {
              navigator.clipboard.writeText(a.intakeCode || '').then(() => toast('Intake code copied'));
            }}
          >
            Copy
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, lineHeight: 1.6 }}>
          This code links the medical intake to this partnership and covers the cost of their first
          prescription.
        </p>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Product Interest</div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{productOf(a)}</div>
      </div>
    </div>
  );
}

function GuidelinesTab({ a }: { a: CreatorAccount }) {
  const dos = [
    'Disclose the paid partnership clearly — #ad, #sponsored, or #KickHealthPartner',
    'Speak from personal experience and tag @kickhealth',
    'Submit content for review at least 5 business days before posting',
    'Film in clean, well-lit environments with clear audio',
  ];
  const donts = [
    'Make specific medical claims or compare to competitors',
    'Post content before it has been reviewed and approved',
    'Share their personal intake code publicly',
    'Use stock footage, AI-generated faces, or AI-generated voiceovers',
  ];
  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Brand Guidelines</div>
        <div className={styles.acTabSub}>Creator's acknowledgment status and content rules.</div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Acknowledgment Status</div>
        {a.brand_guidelines_acknowledged ? (
          <div className={styles.acCompleteCard}>
            <span className={styles.acCompleteCheck}>✓</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Creator acknowledged guidelines</div>
              <div style={{ fontSize: 12, color: '#059669', marginTop: 3 }}>
                {formatDate(a.brand_guidelines_acknowledged_at)}
              </div>
            </div>
          </div>
        ) : (
          <span className={styles.vsBadge} style={{ background: '#fef3c7', color: '#92400e' }}>
            Not yet acknowledged
          </span>
        )}
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Guidelines Reference</div>
        <div className={styles.acGuidelines}>
          {dos.map((t) => (
            <div key={t} className={styles.acGlRow}>
              <span className={`${styles.acGlDot} ${styles.acGlDo}`} />
              {t}
            </div>
          ))}
          {donts.map((t) => (
            <div key={t} className={styles.acGlRow}>
              <span className={`${styles.acGlDot} ${styles.acGlDont}`} />
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VideoTab({
  a,
  onPatch,
}: {
  a: CreatorAccount;
  onPatch: (body: Record<string, unknown>, msg?: string) => void;
}) {
  const [val, setVal] = useState(a.video_status);
  const draft1 = useQuery({
    queryKey: ['admin', 'draft-url', a.id, 1],
    queryFn: () => api.get<{ url: string }>(`/api/admin/creators/${a.id}/drafts/1/url`),
    enabled: !!a.draft_1_url,
    staleTime: 30 * 60_000,
  });
  const draft2 = useQuery({
    queryKey: ['admin', 'draft-url', a.id, 2],
    queryFn: () => api.get<{ url: string }>(`/api/admin/creators/${a.id}/drafts/2/url`),
    enabled: !!a.draft_2_url,
    staleTime: 30 * 60_000,
  });
  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Video Submission</div>
        <div className={styles.acTabSub}>Review submitted drafts and update the review status.</div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Review Status</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', maxWidth: 360 }}>
          <Select value={val} onChange={(e) => setVal(e.target.value)}>
            <option value="awaiting_submission">Awaiting Submission</option>
            <option value="under_review">Under Review</option>
            <option value="revisions_requested">Revisions Requested</option>
            <option value="approved">Approved</option>
          </Select>
          <button
            type="button"
            className={styles.btnDocusign}
            style={{ background: 'var(--accent)' }}
            onClick={() => onPatch({ video_status: val }, 'Video status updated')}
          >
            Save
          </button>
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Draft 1</div>
        {a.draft_1_url && draft1.data?.url ? (
          <video className={styles.acFullVideo} controls src={draft1.data.url} />
        ) : (
          <div className={styles.acDraftEmpty}>No draft 1 submitted yet</div>
        )}
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Draft 2</div>
        {a.draft_2_url && draft2.data?.url ? (
          <video className={styles.acFullVideo} controls src={draft2.data.url} />
        ) : (
          <div className={styles.acDraftEmpty}>No draft 2 submitted yet</div>
        )}
      </div>
    </div>
  );
}

const MSG_MAX_LEN = 5000;
const MSG_POLL_MS = 15_000;

function adminMsgTempId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function MessagesTab({ a, toast }: { a: CreatorAccount; toast: (m: string) => void }) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'creators', a.id, 'messages'] as const;
  const [text, setText] = useState('');
  const threadRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<{ messages: Message[] } | Message[]>(
        `/api/admin/creators/${a.id}/messages`,
      ),
    refetchInterval: MSG_POLL_MS,
  });
  const messages: Message[] = Array.isArray(data) ? data : (data?.messages ?? []);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages.length]);

  function setMsgs(fn: (msgs: Message[]) => Message[]) {
    qc.setQueryData<{ messages: Message[] } | Message[]>(queryKey, (old) => {
      const list = Array.isArray(old) ? old : (old?.messages ?? []);
      return fn(list);
    });
  }

  function sendText(body: string): Promise<void> {
    const tmp = adminMsgTempId();
    const optimistic: Message = {
      id: tmp,
      from: 'admin',
      senderName: 'You',
      text: body,
      sentAt: new Date().toISOString(),
      pending: true,
    };
    qc.cancelQueries({ queryKey });
    setMsgs((msgs) => [...msgs, optimistic]);
    return api
      .post<{ message: Message }>(`/api/admin/creators/${a.id}/messages`, { text: body })
      .then((res) => {
        setMsgs((msgs) => msgs.map((m) => (m.id === tmp ? res.message : m)));
      })
      .catch((err) => {
        setMsgs((msgs) =>
          msgs.map((m) => (m.id === tmp ? { ...m, pending: false, failed: true } : m)),
        );
        throw err;
      })
      .finally(() => qc.invalidateQueries({ queryKey }));
  }

  const send = useMutation<unknown, ApiError, string>({
    mutationFn: (body) => sendText(body),
    onSuccess: () => toast('Message sent'),
    onError: (err) => toast(err.message || 'Failed to send message'),
  });

  function submit() {
    const t = text.trim();
    if (!t || send.isPending) return;
    setText('');
    send.mutate(t);
  }

  function retry(failed: Message) {
    setMsgs((msgs) => msgs.filter((m) => m.id !== failed.id));
    send.mutate(failed.text);
  }

  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Messages</div>
        <div className={styles.acTabSub}>Direct communication with the creator.</div>
      </div>
      <div className={styles.acMsgPanel}>
        <div className={styles.acMsgThread} ref={threadRef}>
          {messages.length === 0 ? (
            <div className={styles.acMsgEmpty}>
              No messages yet — start the conversation below.
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={m.pending ? styles.acMsgPending : ''}>
                <div
                  className={`${styles.acMsgBubble} ${m.from === 'admin' ? styles.fromAdmin : styles.fromCreator} ${m.failed ? styles.acMsgBubbleFailed : ''}`}
                >
                  {m.text}
                </div>
                <div
                  className={styles.acMsgMeta}
                  style={{ textAlign: m.from === 'admin' ? 'right' : 'left' }}
                >
                  {m.pending
                    ? `${m.senderName} · Sending…`
                    : m.failed
                      ? `${m.senderName} · Failed to send`
                      : `${m.senderName} · ${formatDateTime(m.sentAt)}`}
                  {m.failed && (
                    <button
                      type="button"
                      className={styles.acMsgRetry}
                      onClick={() => retry(m)}
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <div className={styles.acMsgCompose}>
          <textarea
            className={styles.acMsgInput}
            rows={3}
            value={text}
            maxLength={MSG_MAX_LEN}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Type a message to the creator…"
          />
          <button
            type="button"
            className={styles.acMsgSend}
            disabled={!text.trim() || send.isPending}
            onClick={submit}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentTab({
  a,
  onPatch,
}: {
  a: CreatorAccount;
  onPatch: (body: Record<string, unknown>, msg?: string) => void;
}) {
  const [amount, setAmount] = useState(String(a.payment_amount ?? 300));
  const [method, setMethod] = useState(a.payment_method || '');
  const [date, setDate] = useState(a.payment_date ? a.payment_date.split('T')[0] : '');
  const [notes, setNotes] = useState(a.payment_notes || '');
  const [paypalEmail, setPaypalEmail] = useState(a.paypal_email || '');
  const [paypalName, setPaypalName] = useState(a.paypal_recipient_name || '');
  const [paypalPhone, setPaypalPhone] = useState(a.paypal_phone || '');
  const status = a.payment_status || 'pending';

  return (
    <div className={styles.acTabBody}>
      <div>
        <div className={styles.acTabTitle}>Payment</div>
        <div className={styles.acTabSub}>
          Manage and track payment to this creator. Payments are issued after video approval.
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Creator-Provided PayPal Info</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
          Submitted by the creator on their payment page. You can edit to correct typos before sending.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'var(--text2)',
              }}
            >
              Legal Name (as on PayPal)
            </label>
            <input
              className={styles.acStatusSelect}
              type="text"
              value={paypalName}
              onChange={(e) => setPaypalName(e.target.value)}
              placeholder="Not provided"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--text2)',
                }}
              >
                PayPal Email
              </label>
              <input
                className={styles.acStatusSelect}
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="Not provided"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--text2)',
                }}
              >
                PayPal Phone
              </label>
              <input
                className={styles.acStatusSelect}
                type="tel"
                value={paypalPhone}
                onChange={(e) => setPaypalPhone(e.target.value)}
                placeholder="Not provided"
              />
            </div>
          </div>
          <div>
            <button
              type="button"
              className={styles.btnDocusign}
              style={{ background: 'var(--accent)' }}
              onClick={() =>
                onPatch(
                  {
                    paypal_email: paypalEmail.trim(),
                    paypal_recipient_name: paypalName.trim(),
                    paypal_phone: paypalPhone.trim(),
                  },
                  'PayPal info saved',
                )
              }
            >
              Save PayPal Info
            </button>
          </div>
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Payment Status</div>
        {status === 'paid' ? (
          <div className={styles.acCompleteCard}>
            <span className={styles.acCompleteCheck}>✓</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Payment sent</div>
              <div style={{ fontSize: 12, color: '#059669', marginTop: 3 }}>
                {formatDate(a.payment_date)}
              </div>
            </div>
          </div>
        ) : status === 'processing' ? (
          <span className={styles.vsBadge} style={{ background: '#ede9fe', color: '#5b21b6' }}>
            Processing
          </span>
        ) : (
          <span className={styles.vsBadge} style={{ background: '#fef3c7', color: '#92400e' }}>
            Pending
          </span>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {status === 'paid' ? (
            <button
              type="button"
              className={`${styles.acProgToggle} ${styles.acMarkUndone}`}
              onClick={() => onPatch({ payment_status: 'pending' }, 'Payment status updated')}
            >
              Mark as Unpaid
            </button>
          ) : (
            <>
              <button
                type="button"
                className={`${styles.acProgToggle} ${styles.acMarkDone}`}
                onClick={() =>
                  onPatch(
                    {
                      payment_status: 'paid',
                      ...(a.payment_date ? {} : { payment_date: new Date().toISOString() }),
                    },
                    'Payment status updated',
                  )
                }
              >
                ✓ Mark as Paid
              </button>
              <button
                type="button"
                className={`${styles.acProgToggle} ${styles.acMarkUndone}`}
                onClick={() =>
                  onPatch(
                    { payment_status: status === 'processing' ? 'pending' : 'processing' },
                    'Payment status updated',
                  )
                }
              >
                Mark as {status === 'processing' ? 'Pending' : 'Processing'}
              </button>
            </>
          )}
        </div>
      </div>
      <div className={styles.acCard}>
        <div className={styles.acCardLabel}>Payment Details</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--text2)',
                }}
              >
                Amount (USD)
              </label>
              <input
                className={styles.acStatusSelect}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--text2)',
                }}
              >
                Payment Method
              </label>
              <Select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                placeholder="— Select —"
              >
                <option>Venmo</option>
                <option>PayPal</option>
                <option>ACH</option>
                <option>Wire Transfer</option>
                <option>Check</option>
                <option>Other</option>
              </Select>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'var(--text2)',
              }}
            >
              Payment Date
            </label>
            <input
              className={styles.acStatusSelect}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'var(--text2)',
              }}
            >
              Notes / Reference
            </label>
            <textarea
              className={styles.acMsgInput}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Confirmation #, transaction ID, or any payment notes…"
            />
          </div>
          <div>
            <button
              type="button"
              className={styles.btnDocusign}
              style={{ background: 'var(--accent)' }}
              onClick={() =>
                onPatch(
                  {
                    payment_amount: parseFloat(amount) || 0,
                    payment_method: method,
                    payment_date: date ? new Date(date).toISOString() : '',
                    payment_notes: notes.trim(),
                  },
                  'Payment details saved',
                )
              }
            >
              Save Payment Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
